import * as React from 'react';

import { Button } from '@/hen-ui/atomics/Button';
import { cn } from '@/hen-ui/utils';
import {
  tabButtonVariants,
  type TabButtonAppearance,
} from './tab-button.variants';

export type TabButtonProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'rel'
> & {
  /** Unselected look: gray outline (default) or neutral gray fill. */
  appearance?: TabButtonAppearance;
};

/**
 * Pill tab trigger for the Tabs `button` look.
 *
 * Renders a hen-ui Button at size `sm` with no Button color variant, so it
 * shares Button's structure and behavior while its rest, hover, pressed,
 * selected, focus and disabled colors come from the `--cds-tab-button-*`
 * tokens. Restyling tabs never changes buttons.
 *
 * Selected styling follows `data-state="active"`, which Radix Tabs sets on the
 * selected trigger. Use it as the `asChild` child of a tabs trigger.
 *
 * @example
 * <TabsTrigger value='about' asChild>
 *   <TabButton appearance='filled'>About</TabButton>
 * </TabsTrigger>
 */
export const TabButton = React.forwardRef<HTMLButtonElement, TabButtonProps>(
  ({ appearance, className, ...props }, ref) => (
    <Button
      ref={ref}
      variant={null}
      size='sm'
      className={cn(tabButtonVariants({ appearance }), className)}
      {...props}
    />
  ),
);

TabButton.displayName = 'TabButton';
